/* eslint-disable @next/next/no-img-element */
import { PLPModalImageFormatter } from "@/components/ProductListing/helper";
import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { HiMiniPrinter } from "react-icons/hi2";
import { TfiClose } from "react-icons/tfi";
// import { staticLabelsCompare } from "./helper";
import PropTypes from "prop-types";
import BackToTop from "@/components/backToTop";
import { getCompareMappings } from "@/utils/compareMappings";
import styles from "./index.module.scss";
import { DEFAULT_IMAGE_LINK } from "@/components/detailsComponent/helper";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";
import { useLocale } from "@/hooks/useLocale";

const EXCLUDED_MAPPING_LABELS = new Set([
  "slug",
  "image",
  "price",
  "skuid",
  "description",
]);

const hasDisplayValue = value => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== undefined && value !== null && String(value).trim() !== "";
};

const formatCellValue = value => {
  if (Array.isArray(value)) {
    if (!value.length) return "-";
    const normalized = value
      .map(item => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object") {
          return item.styleName || item.alt || item.label || item.value || "";
        }
        return String(item);
      })
      .filter(Boolean);
    return normalized.length ? normalized.join(", ") : "-";
  }

  if (value && typeof value === "object") {
    return value.label || value.value || "-";
  }

  return value || "-";
};

const ComparePage = ({ compareMappingsData = {} }) => {
  const locale = useLocale();
  const [products, setProducts] = useState([]);
  const router = useRouter();

  const { staticLabelsCompare } = useContentfulTranslations();

  const compareMappings = useMemo(
    () => getCompareMappings(compareMappingsData),
    [compareMappingsData]
  );

  const mappedRows = useMemo(
    () =>
      Object.entries(compareMappings)
        .filter(
          ([label]) =>
            !EXCLUDED_MAPPING_LABELS.has(String(label).trim().toLowerCase())
        )
        .map(([label]) => ({ label, source: "mapped" })),
    [compareMappings]
  );

  const getRowValue = (product = {}, row = {}) =>
    product?.compareMappedValues?.[row.label];

  const compareRows = useMemo(
    () =>
      mappedRows
        .filter(row =>
          products.some(product => hasDisplayValue(getRowValue(product, row)))
        )
        .sort((a, b) =>
          String(a.label).localeCompare(String(b.label), undefined, {
            sensitivity: "base",
          })
        ),
    [mappedRows, products]
  );

  useEffect(() => {
    const loadProducts = () => {
      const product = JSON.parse(localStorage.getItem("compareProducts")) || [];
      setProducts(product);
    };
    if (typeof window !== "undefined") {
      loadProducts();
    }
  }, []);

  const backHandle = () => {
    history.back();
  };

  const removeProduct = sku => {
    const updatedProducts = products.filter(
      prod => prod?.compareMappedValues?.skuId !== sku
    );
    setProducts(updatedProducts);
    localStorage.setItem("compareProducts", JSON.stringify(updatedProducts));
  };

  const navigateToPdp = (slug, sku) => {
    const pdpUrl = `/product-details/${slug}?sku=${sku}`;
    router.push(pdpUrl);
  };

  const getMappedValue = (product = {}, label = "") =>
    product?.compareMappedValues?.[label];

  const getDisplayImage = (product = {}) => {
    const mappedImage = getMappedValue(product, "image");
    if (Array.isArray(mappedImage) && mappedImage[0]) return mappedImage[0];
    if (typeof mappedImage === "string" && mappedImage) return mappedImage;
    if (Array.isArray(product?.image) && product?.image[0])
      return product?.image[0];
    return product?.image || "";
  };

  const getDisplaySku = (product = {}) =>
    getMappedValue(product, "skuId") || product?.skuId;

  const getDisplaySlug = (product = {}) =>
    getMappedValue(product, "slug") || product?.slug;

  const getDisplayDescription = (product = {}) =>
    getMappedValue(product, "Description") || product?.description;

  const getDisplayBrandName = (product = {}) =>
    getMappedValue(product, "Brand Name") || product?.brandName;

  const getDisplayPrice = (product = {}) =>
    getMappedValue(product, "price") || product?.price;

  const hasSwatchData = products.some(
    product => Array.isArray(product?.swatch) && product?.swatch?.length > 0
  );
  const swatchRowBgClass =
    compareRows.length % 2 ? "bg-[#f5f5f5]" : "bg-[#e5e5e5] print:bg-[#f5f5f5]";

  return (
    <>
      <section
        className={`${styles.compareSection} c-koh-compare-products max-w-screen-lg mx-auto`}
      >
        <h1 className="py-10 text-[32px] font-medium leading-[1.1] text-[#232323] font-[Helvetica-Light,_Helvetica,_sans-serif] print:pt-0 print:pb-5">
          {staticLabelsCompare[locale]?.compareProducts ||
            staticLabelsCompare["en"].compareProducts}
        </h1>
        {products.length ? (
          <div className="overflow-x-auto no-scrollbar">
            <table className=" table-fixed border-collapse border-b border-[#bebebe]">
              <thead>
                <tr>
                  <th className="w-[213px] bg-[#f5f5f5] p-4 text-right display-flex align-bottom justify-end position-relative">
                    <div className="flex justify-end">
                      <button
                        className="flex justify-center items-center mr-3 h-[40px] p-3 w-[40px] bg-[#e5e5e5] text-[#232323] rounded hover:bg-[#364573] hover:text-[#fff] max-[979px]:hidden"
                        onClick={() => window?.print()}
                        aria-label="Print"
                      >
                        <HiMiniPrinter size={20} />
                      </button>
                    </div>
                  </th>
                  {products.map((p, i) => (
                    <th
                      key={i}
                      className="bg-[#f5f5f5] w-[251px] p-1 pb-10 text-center w-1/3 align-top  "
                    >
                      <div className="flex justify-end pb-[80px] pr-2 pt-2">
                        <button
                          onClick={() =>
                            removeProduct(p?.compareMappedValues?.skuId)
                          }
                        >
                          <TfiClose
                            size={15}
                            color="#999"
                            className="cursor-pointer"
                          />
                        </button>
                      </div>

                      <div
                        onClick={() =>
                          navigateToPdp(getDisplaySlug(p), getDisplaySku(p))
                        }
                        className="cursor-pointer"
                      >
                        <div className="h-[188px] flex items-center justify-center shrink-0">
                          <img
                            src={PLPModalImageFormatter(getDisplayImage(p))}
                            alt={getDisplayBrandName(p)}
                            className="mx-auto mb-2 h-full w-[251px] max-w-[251px]"
                            onError={e =>
                              (e.target.src =
                                p?.fallbackImage || DEFAULT_IMAGE_LINK)
                            }
                          />
                        </div>
                        <p className="leading-tight line-clamp-4 text-sm tracking-[0.8px] text-[#666] font-light  mt-[3px] mb-[15px] px-[6px] min-h-[40px] text-[14px] leading-[16px] text-[#232323] max-h-[98px] overflow-hidden font-[Helvetica-Medium,Helvetica,sans-serif] ">
                          {getDisplayDescription(p)}
                        </p>
                        <p className="text-xs font-normal text-[12px] leading-none text-[#666] font-[helveticaRoman,Helvetica,sans-serif]">
                          {getDisplaySku(p)}
                        </p>
                        <p className="text-sm font-semibold">
                          {getDisplayPrice(p)}
                        </p>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${idx % 2 ? "bg-[#f5f5f5]" : "bg-[#e5e5e5] print:bg-[#f5f5f5]"}`}
                  >
                    <td className="border-r border-[#bebebe] w-50 pt-[10px] pb-[10px] pl-3.75 text-[#232323] text-[14px] leading-5 min-h-10.5 font-[helveticaRoman,Helvetica,sans-serif]">
                      {row.label}
                    </td>
                    {products.map((prod, i) => (
                      <td
                        key={i}
                        className={`${i === 1 || i === 0 ? "border-r border-[#bebebe]" : ""} font-[HelveticaBold,Helvetica,sans-serif] p-2 text-center`}
                      >
                        {formatCellValue(getRowValue(prod, row))}
                      </td>
                    ))}
                  </tr>
                ))}
                {hasSwatchData && (
                  <tr className={`${swatchRowBgClass}`}>
                    <td className="border-r border-[#bebebe] w-50 pt-[10px] pb-[10px] pl-3.75 text-[#232323] text-[14px] leading-5 min-h-10.5 font-[helveticaRoman,Helvetica,sans-serif]">
                      Color/Finishes
                    </td>
                    {products.map((product, index) => (
                      <td
                        key={`swatch-${index}`}
                        className={`${index === 1 || index === 0 ? "border-r border-[#bebebe]" : ""} font-[HelveticaBold,Helvetica,sans-serif] p-2 text-center`}
                      >
                        {Array.isArray(product?.swatch) &&
                        product?.swatch?.length > 0 ? (
                          <div className="flex justify-center flex-wrap gap-1">
                            {product.swatch.map(swatch => (
                              <img
                                key={swatch?.skuid || swatch?.src}
                                src={swatch?.src}
                                alt={swatch?.styleName || "swatch"}
                                title={swatch?.styleName || "swatch"}
                                className="w-5 h-5 border border-[#ccc]"
                              />
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>{" "}
          </div>
        ) : (
          <hr className="h-0 border-0 border-t border-solid border-[#bebebe]" />
        )}

        <div className="text-center py-10">
          <button
            onClick={backHandle}
            className="bg-[#e5e5e5] hover:bg-[#bebebe] border-0 rounded-[4px] text-[#232323] font-[HelveticaBold,Helvetica,sans-serif] text-[13px] leading-[1em] py-[13.5px] px-[20px] shadow-[0_2px_2px_0_rgba(0,0,0,0.2)] font-normal text-center uppercase no-underline outline-none cursor-pointer"
          >
            {staticLabelsCompare[locale]?.back}
          </button>
        </div>
      </section>
      <BackToTop topHeight={0} localeProp={locale} defaultThreshold={"400"} />
    </>
  );
};

ComparePage.propTypes = {
  locale: PropTypes.string,
  compareMappingsData: PropTypes.object,
};

export default ComparePage;
